import {IMAGES} from "../utils/staticJSON";
import {Offcanvas} from "react-bootstrap";
import React, {useEffect, useState} from "react";
import {NavLink} from "react-router-dom";
import {Link} from "react-router-dom";
import LOGO from "../assets/media/svg/logo.svg";

interface HeaderProps {
    showButton: boolean;
}

export const Header: React.FC<HeaderProps> = ({showButton}) => {
    const [show, setShow] = useState(false);
    const [isScrolled, setIsScrolled] = useState(true);
    const [activeSection, setActiveSection] = useState<string>("home"); // Track active section

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    useEffect(() => {
        const handleScroll = () => {
            const scrollThreshold = 50;
            if (window.scrollY > scrollThreshold) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener("scroll", handleScroll);

        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    }, []);

    // IntersectionObserver to detect active section
    useEffect(() => {
        const sections = ["home", "features", "about-us", "faqs", "contact-us"];

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setActiveSection(entry.target.id);
                    }
                });
            },
            {threshold: 0.4} // Trigger when 50% of the section is visible
        );

        sections.forEach((sectionId) => {
            const section = document.getElementById(sectionId);
            if (section) {
                observer.observe(section);
            }
        });

        return () => {
            sections.forEach((sectionId) => {
                const section = document.getElementById(sectionId);
                if (section) {
                    observer.unobserve(section);
                }
            });
        };
    }, []);

    const scrollToSection = (event: any, sectionId: string) => {
        event.preventDefault();

        const section = document.getElementById(sectionId);
        const navbarHeight = document.querySelector("nav")?.offsetHeight;

        if (section) {
            const sectionTop = section.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
                top: navbarHeight && sectionTop - navbarHeight,
                behavior: "smooth",
            });

            setActiveSection(sectionId); // Update active section when clicked
        }
    };

    return (
        <header className="wrapper">
            <nav
                className={`navbar navbar-expand-lg center-nav navbar-light navbar-bg-light py-0 ${
                    isScrolled ? "scrolled" : ""
                }`}
                style={{
                    position: "fixed",
                    backgroundColor: isScrolled ? "#007B8B" : "transparent",
                    transition: "background-color 0.3s ease",
                }}
            >
                <div className="container flex-lg-row flex-nowrap align-items-center">
                    <div className="navbar-brand">
                        <NavLink to="/">
                            <img className="" src={LOGO} alt="" />
                        </NavLink>
                    </div>
                    <div className="navbar-collapse offcanvas offcanvas-nav offcanvas-start">
                        <ul className="navbar-nav">
                            <li className="nav-item">
                                <a
                                    href="#home"
                                    onClick={(e) => scrollToSection(e, "home")}
                                    className={`nav-link fs-16 fw-600 ${activeSection === "home" ? "text-white" : ""}`}
                                >
                                    Home
                                </a>
                            </li>
                            <li className="nav-item">
                                <a
                                    href="#features"
                                    onClick={(e) => scrollToSection(e, "features")}
                                    className={`nav-link fs-16 fw-600 ${
                                        activeSection === "features" ? "text-white" : ""
                                    }`}
                                >
                                    Features
                                </a>
                            </li>
                            <li className="nav-item">
                                <a
                                    href="#about-us"
                                    onClick={(e) => scrollToSection(e, "about-us")}
                                    className={`nav-link fs-16 fw-600 ${
                                        activeSection === "about-us" ? "text-white" : ""
                                    }`}
                                >
                                    About Us
                                </a>
                            </li>
                            <li className="nav-item">
                                <a
                                    href="#faqs"
                                    onClick={(e) => scrollToSection(e, "faqs")}
                                    className={`nav-link fs-16 fw-600 ${activeSection === "faqs" ? "text-white" : ""}`}
                                >
                                    FAQs
                                </a>
                            </li>
                            <li className="nav-item">
                                <a
                                    href="#contact-us"
                                    onClick={(e) => scrollToSection(e, "contact-us")}
                                    className={`nav-link fs-16 fw-600 ${
                                        activeSection === "contact-us" ? "text-white" : ""
                                    }`}
                                >
                                    Contact Us
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div className="navbar-collapse offcanvas offcanvas-nav offcanvas-start">
                        <Offcanvas show={show} onHide={handleClose}>
                            <Offcanvas.Header className="offcanvas-header" closeButton>
                                <Link to="/">
                                    <img
                                        className="img-fluid"
                                        width={125}
                                        height={125}
                                        src={IMAGES.uLearnLogoWhite}
                                        alt=""
                                    />
                                </Link>
                                <button
                                    type="button"
                                    className="btn-close btn-close-white text-white"
                                    onClick={handleShow}
                                />
                            </Offcanvas.Header>
                            <Offcanvas.Body className="offcanvas-body ms-lg-auto d-flex flex-column h-100">
                                <ul className="navbar-nav">
                                    <li className="nav-item">
                                        <a href="#home" className="nav-link">
                                            Home
                                        </a>
                                    </li>
                                    <li className="nav-item">
                                        <a href="#features" className="nav-link">
                                            Features
                                        </a>
                                    </li>
                                    <li className="nav-item">
                                        <a href="#about-us" className="nav-link">
                                            About Us
                                        </a>
                                    </li>
                                    <li className="nav-item">
                                        <a href="#faqs" className="nav-link">
                                            FAQs
                                        </a>
                                    </li>
                                    <li className="nav-item">
                                        <a href="#contact-us" className="nav-link">
                                            Contact Us
                                        </a>
                                    </li>
                                </ul>
                            </Offcanvas.Body>
                        </Offcanvas>
                    </div>
                    <div className="navbar-other w-100 d-flex ms-auto">
                        <ul className="navbar-nav flex-row align-items-center ms-auto">
                            <li className="nav-item d-none d-md-block">
                                <a
                                    // href="#"
                                    onClick={(e) => scrollToSection(e, "contact-us")}
                                    className="btn btn-dark fs-16 fw-600"
                                >
                                    Book a Demo
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>
        </header>
    );
};

export default Header;
